import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  cancelSubscriptionApi,
  createPaymentLinkApi,
  deletePaymentLinkApi,
  fetchBillingOverview,
  fetchBillingProducts,
  fetchMyBilling,
  resumeSubscriptionApi,
  setStripeCustomerApi,
  type CreatePaymentLinkBody,
} from "@/lib/api";

const BILLING_KEY = ["super-admin", "billing"] as const;

export function useBillingOverview() {
  return useQuery({
    queryKey: BILLING_KEY,
    queryFn: fetchBillingOverview,
  });
}

function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: BILLING_KEY });
}

export function useSetStripeCustomer() {
  const invalidate = useInvalidateBilling();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, stripeCustomerId }: { clientId: string; stripeCustomerId: string | null }) =>
      setStripeCustomerApi(clientId, stripeCustomerId),
    onSuccess: (result, { stripeCustomerId }) => {
      void invalidate();
      // The clients list shows the same link, so it goes stale on this write too.
      void queryClient.invalidateQueries({ queryKey: ["super-admin", "clients"] });
      if (!stripeCustomerId) {
        toast.success("Cliente de Stripe desvinculado");
        return;
      }
      // Adoption is the non-obvious part of linking: events that arrived before the
      // id was pasted are attached now, so say so rather than let history appear
      // from nowhere.
      toast.success(
        result.adoptedEvents > 0
          ? `Vinculado — se recuperaron ${result.adoptedEvents} evento(s) pendiente(s)`
          : "Cliente de Stripe vinculado",
      );
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo vincular el cliente de Stripe"),
  });
}

/** The school's own view of its subscription. Server rejects `member`. */
export function useMyBilling() {
  return useQuery({
    queryKey: ["billing", "me"],
    queryFn: fetchMyBilling,
    staleTime: 5 * 60_000,
  });
}

// Per client: the server offers only tiers of the client's own brand.
export function useBillingProducts(clientId: string) {
  return useQuery({
    queryKey: ["super-admin", "billing", "products", clientId],
    queryFn: () => fetchBillingProducts(clientId),
    // The tier list changes only when someone adds a Product in Stripe.
    staleTime: 5 * 60_000,
  });
}

export function useCreatePaymentLink() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: ({ clientId, ...body }: CreatePaymentLinkBody & { clientId: string }) =>
      createPaymentLinkApi(clientId, body),
    onSuccess: () => {
      void invalidate();
      toast.success("Link de cobro generado");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo generar el link"),
  });
}

export function useDeletePaymentLink() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (clientId: string) => deletePaymentLinkApi(clientId),
    onSuccess: () => {
      void invalidate();
      toast.success("Link desactivado");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo desactivar el link"),
  });
}

export function useCancelSubscription() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (clientId: string) => cancelSubscriptionApi(clientId),
    onSuccess: () => {
      void invalidate();
      toast.success("La suscripción termina al final del periodo");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo cancelar"),
  });
}

export function useResumeSubscription() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (clientId: string) => resumeSubscriptionApi(clientId),
    onSuccess: () => {
      void invalidate();
      toast.success("Suscripción reactivada");
    },
    onError: (err: Error) => toast.error(err.message || "No se pudo reactivar"),
  });
}
